import * as React from "react";
import styles from "./user-navigation.css";
import { RouteComponentProps, withRouter } from "react-router";
import { actionCreators } from "page/calendar/store";
import { actionCreators as uiActionCreators } from "ui/store";
import { actionCreators as userActionCreators } from "user/store";
import * as cn from "classnames/bind";
import { useState, useEffect } from "react";
import {
    UserPrintInfoInformation,
    CalendarState,
    LayoutFormatOptions,
    LayoutFormatResource,
    ImageDetails,
    CustomImage,
    SpiralItem,
    RubberBandItem,
} from "burde";
import {
    getSavedCalendarsForUser,
    saveCalendarWithBurde,
    getLayoutFormatOptions,
    deleteCalendar,
} from "calendar/service";
import { signOutUser } from "page/user/service";
import { NavigationComponents, DefaultSize, DefaultType } from "ui/constants";
import { COOKIE_USER_JWT } from "ui/constants";
import { getComponentRoute } from "page/calendar/helper";
import { getUserDesigns } from "burde-api-content/burde-user-service";
import Cookies from "js-cookie";

const cx = cn.bind(styles);

const userNavCn = (isVisible: boolean) =>
    cx({
        userNav: true,
        userNavVisible: isVisible,
    });

const userNavCloseButtonCn = () =>
    cx({
        userNavCloseButton: true,
    });

const innerNavCn = () =>
    cx({
        innerNav: true,
    });

const innerNavInputCn = () =>
    cx({
        innerNavInput: true,
    });

const userNavOverlayCn = (menuVisible: boolean) =>
    cx({
        userNavOverlay: true,
        userNavOverlayVisible: menuVisible,
    });

type UserNavigationProps = {
    UserNav_User: string;
    UserNav_Admin: string;
    UserNav_Orderhistory: string;
    UserNav_Logout: string;
    UserNav_Design_Header: string;
    UserNav_Design_Checkbox_Theme: string;
    UserNav_Design_Name_Placeholder: string;
    UserNav_Design_Button_SaveAsTheme: string;
    UserNav_Design_Button_Save: string;
    UserNav_Design_List_Header: string;
    Calendar_Size: string;
    showUserMenu: boolean;
    calendar: CalendarState;
    isAuthenticated: boolean;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiralItem: SpiralItem | null;
    rubberbandItem: RubberBandItem | null;
    domain: string;
} & typeof uiActionCreators &
    typeof userActionCreators &
    typeof actionCreators;

const UserNavigation: React.FC<UserNavigationProps & RouteComponentProps> = (
    props,
) => {
    const [savedCalendars, setSavedCalendars] = useState<
        Array<UserPrintInfoInformation>
    >([]);
    const [activeCalendarName, setActiveCalendarName] = useState("");
    const [isTheme, setIsTheme] = useState<boolean>(props.calendar.isTheme);

    const [layoutFormatOptions, setLayoutFormatOptions] = useState<
        LayoutFormatOptions
    >();

    const {
        UserNav_User,
        UserNav_Logout,
        UserNav_Design_Header,
        UserNav_Design_Name_Placeholder,
        UserNav_Design_Button_SaveAsTheme,
        UserNav_Design_Button_Save,
        UserNav_Design_List_Header,
        Calendar_Size,
        history,
        domain,
    } = props;

    const fetchLayoutFormatOptions = async () => {
        const layoutFormatOptions = await getLayoutFormatOptions();

        if (layoutFormatOptions) {
            setLayoutFormatOptions(layoutFormatOptions);
        }
    };

    const getSavedCalendars = async () => {
        const calendars = await getSavedCalendarsForUser();
        setSavedCalendars(calendars);
    };

    const changeName = ({
        target: { value: name },
    }: React.ChangeEvent<HTMLInputElement>) => {
        setActiveCalendarName(name);
    };

    const saveDesign = () => {
        const { calendar } = props;
        calendar.name = activeCalendarName;

        saveDesignHelper(calendar);
        props.UpdateShowUserMenu(false);
    };

    const toggleTheme = () => setIsTheme(!isTheme);

    const saveDesignHelper = async (calendar: CalendarState) => {
        const {
            isAuthenticated,
            setActiveCalendar /*, onSaveClicked*/,
        } = props;

        if (!isAuthenticated) {
            return;
        }

        try {
            const savedCalendarState = await saveCalendarWithBurde(calendar);

            if (savedCalendarState !== null) {
                setActiveCalendar(savedCalendarState);
                getSavedCalendars();
            }
        } catch {
            // do nothing, calendar hasn't been saved and won't be updated in the client
        }
    };

    const openSavedCalendar = async (calendarId: number) => {
        const savedCalendars = await getUserDesigns();

        if (savedCalendars == null) return; //TODO: Handle better

        const match = savedCalendars.find((x) => x.id == calendarId);

        if (match) {
            setActiveCalendarName(match.name);

            const calendarSize =
                match.calendarSize !== undefined && match.calendarSize !== ""
                    ? match.calendarSize
                    : DefaultSize;

            const calendarType =
                match.calendarType !== undefined
                    ? match.calendarType
                    : DefaultType;

            props.setActiveCalendar(match);
            props.UpdateActiveNavigationComponent(NavigationComponents[0]);

            const route = getComponentRoute(
                NavigationComponents[0].route,
                calendarSize,
                calendarType,
            );

            history.push(route);
        }
    };

    const removeCalendar = async (id: number) => {
        const { isAuthenticated, domain } = props;

        if (!isAuthenticated) {
            return;
        }

        try {
            const deleted = await deleteCalendar(id);

            if (deleted) {
                getSavedCalendars();
            }
        } catch {
            // TODO: Improve this.
            // do nothing, calendar hasn't been deleted and won't be updated in the client
        }
    };

    const signOut = async () => {
        const { success } = await signOutUser();
        if (success) {
            props.UpdateShowUserMenu(false);
            props.updateUser({
                id: "",
                roles: [],
                authenticated: false,
                userName: "",
                email: "",
                softDeleted: false,
            });

            Cookies.remove(COOKIE_USER_JWT, {
                domain,
            });

            window.location.href = "/";
        }
    };

    const setLayoutFormat = (option: LayoutFormatResource) => {
        window.location.href = option.link;
    };

    useEffect(() => {
        fetchLayoutFormatOptions();
    }, []);

    useEffect(() => {
        if (props.showUserMenu === true) {
            getSavedCalendars();
        }
    }, [props.showUserMenu]);

    useEffect(() => {
        if (props.calendar.name != activeCalendarName) {
            setActiveCalendarName(props.calendar.name);
        }

        if (props.calendar.isTheme !== isTheme) {
            setIsTheme(props.calendar.isTheme);
        }
    }, [props.calendar]);

    return (
        <>
            <div className={userNavCn(props.showUserMenu)}>
                <button
                    className={userNavCloseButtonCn()}
                    onClick={() => props.UpdateShowUserMenu(false)}>
                    <i className={"fas fa-times"} />
                </button>
                <div className={innerNavCn()}>
                    <label>{Calendar_Size}</label>
                    <ul
                        className={[
                            styles.iconPicker,
                            styles.iconPickerSmall,
                        ].join(" ")}>
                        {layoutFormatOptions &&
                            layoutFormatOptions.layoutFormatResources &&
                            layoutFormatOptions.layoutFormatResources.map(
                                (option) => {
                                    return (
                                        <li
                                            key={option.title}
                                            className={styles.iconPickerLi}
                                            onClick={() => {
                                                setLayoutFormat(option);
                                            }}>
                                            <div
                                                className={[
                                                    styles.iconPickerItem,
                                                    styles.iconPickerItemImageAuto,
                                                    history.location.pathname.includes(
                                                        option.title.toLowerCase(),
                                                    )
                                                        ? styles.iconPickerItemSelected
                                                        : "",
                                                ].join(" ")}>
                                                <div>
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                option.thumbnailSvg,
                                                        }}></div>
                                                    {option.title}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                },
                            )}
                    </ul>
                    <label>{UserNav_User}</label>
                    <ul>
                        <li>
                            <a onClick={signOut}>
                                <i className={"fas fa-unlock"} />{" "}
                                {UserNav_Logout}
                            </a>
                        </li>
                    </ul>
                    <label>{UserNav_Design_Header}</label>
                    <div className={"row"}>
                        <div className={"col-8"}>
                            <input
                                className={innerNavInputCn()}
                                value={activeCalendarName}
                                required={true}
                                type={"text"}
                                placeholder={UserNav_Design_Name_Placeholder}
                                onChange={changeName}
                            />
                        </div>
                        <div className={"col-4"}>
                            <button onClick={saveDesign}>
                                {isTheme
                                    ? UserNav_Design_Button_SaveAsTheme
                                    : UserNav_Design_Button_Save}
                            </button>
                        </div>
                    </div>
                    <label>{UserNav_Design_List_Header}</label>
                    <ul>
                        {savedCalendars &&
                            savedCalendars.map((calendar, index) => (
                                <li key={calendar.name + index}>
                                    <a
                                        style={{
                                            width: "90%",
                                            float: "left",
                                        }}
                                        onClick={() =>
                                            openSavedCalendar(
                                                calendar.calendarId,
                                            )
                                        }>
                                        <i
                                            style={{
                                                marginRight: "4px",
                                            }}
                                            className={"fas fa-calendar-check"}
                                        />{" "}
                                        {calendar.name} (
                                        {calendar.calendarSize || "A5"})
                                    </a>
                                    <i
                                        onClick={() =>
                                            removeCalendar(calendar.calendarId)
                                        }
                                        className={[
                                            styles.removeDesignIcon,
                                            "fas fa-trash",
                                        ].join(" ")}></i>
                                </li>
                            ))}
                    </ul>
                </div>
            </div>
            <div
                className={userNavOverlayCn(props.showUserMenu)}
                onClick={() => props.UpdateShowUserMenu(false)}
            />
        </>
    );
};

export default withRouter(UserNavigation);
